@use "Styles/mixins";
@use "Styles/includes/variables";

* {
    box-sizing: border-box;
}

// The host never participates in layout itself — the inline card flows in the
// content column and the floating bar is fixed-positioned.
:host {
    display: contents;
}

// In default (non-preview) mode the floating bar is mobile-only; the desktop shows
// the inline card instead.
:host(:not([display-isi-preview])) .vm-video-isi__floating {
    @include mixins.tablet-and-up {
        display: none;
    }
}

// -- Inline card (desktop default) --
// Lives in the main content column — hidden on mobile, where the floating bar
// (or the bottom sheet) surfaces the ISI instead.
.vm-video-isi__card {
    margin-top: 8px;

    padding: 24px;
    border-radius: 16px;
    background-color: #{mixins.get-color(background, bg-gray-100)};
    color: mixins.get-color(text, text-secondary);
    font-size: mixins.get-font-size(text-sm);
    line-height: mixins.get-line-height(text-sm);
    display: none;

    @include mixins.desktop-and-up {
        display: block;
    }

    ::slotted(p),
    p {
        margin: 0 0 12px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        color: mixins.get-color(text, text-primary);
        font-weight: 700;
        margin: 16px 0 8px;

        &:first-child {
            margin-top: 0;
        }
    }
    @include mixins.unsafe-html-links;
}

// -- Floating bar (display_isi_preview mode) --
// Pinned to the viewport bottom and layered above the site header via the
// dedicated safety-information z-index so the ISI is never obscured.
.vm-video-isi__floating {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: mixins.get-z(safety-information, element);
    background-color: mixins.get-color(bg, solid, primary);
    border-top: 1px solid mixins.get-color(border, border-secondary);
    font-family: variables.$fontQuaternary;
}

.vm-video-isi__toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: mixins.get-color(text, text-primary);
}

.vm-video-isi__chevron-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    transition: transform 0.3s ease;
    transform: rotate(180deg); // points up while collapsed (tap to expand)

    .vm-video-isi__floating--expanded & {
        transform: rotate(0deg); // points down while expanded (tap to collapse)
    }
}

.vm-video-isi__floating-body {
    // Right padding keeps the first line clear of the toggle button.
    padding: 16px 48px 32px 16px;
    // Collapsed: a capped, scrollable window so the full ISI is reachable
    // without expanding.
    max-height: calc(#{mixins.get-line-height(text-xs)} * 10 + 48px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transition: max-height 0.4s ease;
    color: mixins.get-color(text, text-primary);
    font-size: mixins.get-font-size(text-xs);
    line-height: mixins.get-line-height(text-xs);

    .vm-video-isi__floating--expanded & {
        max-height: 100dvh;

        // Expand to the full viewport on mobile.
        @include mixins.desktop-and-up {
            max-height: 60dvh;
        }
    }

    p {
        margin: 0 0 12px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        color: mixins.get-color(text, text-primary);
        font-weight: 700;
        margin: 0 0 8px;
        font-size: mixins.get-font-size(text-xl);
        line-height: mixins.get-line-height(text-xl);

        &:first-child {
            margin-top: 0;
        }
    }
    @include mixins.unsafe-html-links;
}
